import React, { useEffect, useId, useRef, useState } from "react";
import { Checkbox, GlobalStyles, Radio } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { CmpNewsletterRecipientPreviewRecipientStatusEnum } from "../../../graphql/types";
import { InvoiceDesignScope } from "../Invoicing/InvoiceDesignSystem";
import { inlineChoiceControlSx } from "../../common/dashboard/formStyles";
import {
  EmailAudienceMemberOption,
  EmailFormState,
  NewsletterMemberFilters,
  RecipientGroup,
  RecipientPreviewSummary,
  RecipientSourceId,
  buildDefaultNewsletterMemberFilters,
  nonMemberSubscribersRecipientSourceLabel,
} from "./emailMockData";
import { isEmailImageFile } from "./emailAssetUpload";

export type EmailFormMode = "create" | "update";
export type AudienceFilterId = keyof NewsletterMemberFilters;
export type AudienceFilters = NewsletterMemberFilters;

export type UpdateEmailField = <FieldName extends keyof EmailFormState>(
  field: FieldName,
  value: EmailFormState[FieldName]
) => void;

// SES v1 caps the complete base64-encoded message at 10MB. Keeping raw
// attachments at 6MB total leaves room for MIME encoding and the email body.
export const maxTotalAttachmentBytes = 6 * 1024 * 1024;
export const maxAttachmentBytes = maxTotalAttachmentBytes;
export const maxAttachmentCount = 10;
export const allowedAttachmentTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
];
export const attachmentAccept = [".pdf", ".doc", ".docx", "image/*"].join(",");

export const isAllowedEmailAttachment = ({ file }: { file: File }): boolean =>
  isEmailImageFile({ file }) || allowedAttachmentTypes.includes(file.type);

export const pageTitleClassName =
  "font-['DM_Sans'] text-[28px] font-semibold leading-tight text-[#0A0A0A]";
export const pageDescriptionClassName =
  "mt-2 font-['DM_Sans'] text-[14px] leading-[1.55] text-[#0A0A0A]";
export const stepTitleClassName =
  "text-[24px] font-bold leading-tight text-[#0A0A0A]";
export const stepDescriptionClassName =
  "mt-2 text-[14px] leading-[1.55] text-[#0A0A0A]";
export const sectionTitleClassName =
  "text-[16px] font-semibold leading-6 text-[#0A0A0A]";
export const sectionDescriptionClassName =
  "mt-2 text-[14px] leading-[1.55] text-[#0A0A0A]";
export const fieldLabelClassName =
  "text-[14px] font-semibold leading-6 text-[#0A0A0A]";
export const fieldHelperClassName =
  "mt-1.5 block text-[14px] leading-5 text-black";
export const campaignFieldLabelClassName =
  "text-[16px] font-semibold leading-6 text-[#0A0A0A]";
export const campaignFieldHelperClassName =
  "mt-2 block text-[14px] leading-5 text-black";
export const inputClassName =
  "h-[43px] w-full rounded-lg border border-[#9E9E9E] bg-white px-[14px] text-[14px] text-[#0A0A0A] outline-none transition placeholder:text-[#9E9E9E] focus:border-cpaGold focus:ring-2 focus:ring-cpaGold/20 disabled:bg-gray-100 disabled:text-gray-500";
export const textareaClassName =
  "min-h-[100px] w-full rounded-lg border border-[#9E9E9E] bg-white px-[14px] py-[11px] text-[14px] text-[#0A0A0A] outline-none transition placeholder:text-[#9E9E9E] focus:border-cpaGold focus:ring-2 focus:ring-cpaGold/20 disabled:bg-gray-100 disabled:text-gray-500";
export const sectionClassName =
  "rounded-xl border border-[#ECECEC] bg-white p-5 md:p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04)]";
export const primaryButtonClassName =
  "inline-flex min-h-[43px] items-center justify-center gap-2 rounded-lg border border-cpaGold bg-cpaGold px-[22px] text-[14px] font-semibold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:opacity-60";
export const secondaryButtonClassName =
  "inline-flex min-h-[43px] items-center justify-center gap-2 rounded-lg border border-[#9E9E9E] bg-white px-[22px] text-[14px] font-semibold text-[#0A0A0A] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60";
export const dangerButtonClassName =
  "inline-flex min-h-[43px] items-center justify-center gap-2 rounded-lg border border-[#F21B1B] bg-[#F21B1B] px-[22px] text-[14px] font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60";
export const successButtonClassName =
  "inline-flex min-h-[43px] items-center justify-center gap-2 rounded-lg border border-[#10B981] bg-[#10B981] px-6 text-[14px] font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";

export const darkButtonClassName = `${primaryButtonClassName} !border-[#23272F] !bg-[#23272F] hover:!bg-[#383E48]`;

// Keep the shared dashboard primitives unchanged for their other consumers.
export const EmailDesignScope = ({
  children,
  page = false,
}: {
  children: React.ReactNode;
  page?: boolean;
}) => (
  <InvoiceDesignScope>
    <GlobalStyles
      styles={{
        ".cmp-email-design": { color: "#0A0A0A", minWidth: 0 },
        ".cmp-email-design section[id^='email-step']": {
          borderColor: "#ECECEC",
          borderRadius: 12,
          padding: 28,
          boxShadow: "0 1px 2px rgba(0,0,0,.04)",
        },
        ".cmp-email-design section[id^='email-step'] > div:first-of-type h2": {
          fontSize: 16,
          fontWeight: 600,
          lineHeight: "24px",
        },
        ".cmp-email-design section[id^='email-step'] > div:first-of-type p": {
          fontSize: 14,
          lineHeight: 1.55,
        },
        ".cmp-email-design section[id^='email-step1-'] > div:first-of-type h2, .cmp-email-design section[id^='email-step2-'] > div:first-of-type h2":
          {
            color: "#0A0A0A",
          },
        ".cmp-email-design section[id^='email-step1-'] > div:first-of-type p, .cmp-email-design section[id^='email-step2-'] > div:first-of-type p":
          {
            color: "#0A0A0A",
            marginTop: 4,
          },
        ".cmp-email-design section[id^='email-step'] > div:first-of-type:has(> span), .cmp-email-design section[id^='email-step'] > div:first-of-type > div:first-of-type:has(> span)":
          { columnGap: 10 },
        ".cmp-email-design section[id^='email-step'] > div:first-of-type > span, .cmp-email-design section[id^='email-step'] > div:first-of-type > div:first-of-type > span":
          {
            width: 24,
            height: 24,
            marginTop: 0,
            fontSize: 13,
            fontWeight: 700,
          },
        ".cmp-email-design nav[aria-label$='email steps']": {
          margin: 0,
          top: 64,
          width: "100%",
        },
        ".cmp-email-design nav[aria-label$='email steps'] ol": {
          paddingLeft: 0,
          paddingRight: 0,
        },
        ".cmp-email-design nav[aria-label$='email steps'] button > span.rounded-full":
          { width: 30, height: 30 },
        ".cmp-email-design nav[aria-label$='email steps'] button > span > span:first-of-type":
          { fontSize: 15, fontWeight: 700 },
        ".cmp-email-design > div > div > .max-w-\\[1180px\\]": {
          maxWidth: "none",
        },
        "@media (min-width: 768px)": {
          ".cmp-email-design nav[aria-label$='email steps']": { top: 0 },
        },
        "@media (min-width: 1024px)": {
          ".cmp-email-design-page": { paddingLeft: 8, paddingRight: 8 },
        },
        "@media (max-width: 767px)": {
          ".cmp-email-design section[id^='email-step']": { padding: 20 },
        },
      }}
    />
    <div className={`cmp-email-design${page ? " cmp-email-design-page" : ""}`}>
      {children}
    </div>
  </InvoiceDesignScope>
);

export const defaultAudienceFilters: AudienceFilters =
  buildDefaultNewsletterMemberFilters();

const legacyMemberSizeOptions = [
  { id: "individual", label: "Individual" },
  { id: "2-9", label: "2-9 employees" },
  { id: "10-19", label: "10-19 employees" },
  { id: "20-29", label: "20-29 employees" },
  { id: "30-49", label: "30-49 employees" },
  { id: "50-99", label: "50-99 employees" },
  { id: "100-199", label: "100-199 employees" },
  { id: "200-499", label: "200-499 employees" },
  { id: "500-999", label: "500-999 employees" },
  { id: "1000-1999", label: "1000-1999 employees" },
  { id: "2000+", label: "2000+ employees" },
];
const legacyMemberSinceOptions = [
  { id: "<1", label: "<1 year" },
  { id: "1-3", label: "1-3 years" },
  { id: "4-9", label: "4-9 years" },
  { id: "10-19", label: "10-19 years" },
  { id: "20-29", label: "20-29 years" },
  { id: "30-39", label: "30-39 years" },
  { id: "40-49", label: "40-49 years" },
  { id: "50+", label: "50+ years" },
];
const legacyRenewalStatusOptions = [
  { id: "<30", label: "Less than 30 days out" },
  { id: "31-45", label: "31-45 days out" },
  { id: "46-60", label: "46-60 days out" },
  { id: "61-90", label: "61-90 days out" },
  { id: "91+", label: "91+ days out" },
];

export const memberSizeOptions = [
  { id: "employees:1", label: "1 employee" },
  { id: "employees:2-5", label: "2-5 employees" },
  { id: "employees:6-15", label: "6-15 employees" },
  { id: "employees:16-25", label: "16-25 employees" },
  { id: "employees:26-50", label: "26-50 employees" },
  { id: "employees:51-100", label: "51-100 employees" },
  { id: "employees:101-500", label: "101-500 employees" },
  { id: "employees:501-1000", label: "501-1000 employees" },
  { id: "employees:1001-1500", label: "1001-1500 employees" },
  { id: "employees:1501+", label: "1501+ employees" },
];
export const memberSinceOptions = [
  { id: "years:<1", label: "Less than 1 year" },
  { id: "years:1-3", label: "1-3 years" },
  { id: "years:3-5", label: "3-5 years" },
  { id: "years:5-10", label: "5-10 years" },
  { id: "years:10+", label: "10+ years" },
];
export const renewalStatusOptions = [
  { id: "within:30", label: "Renewing within 30 days" },
  { id: "within:60", label: "Renewing within 60 days" },
  { id: "within:90", label: "Renewing within 90 days" },
  { id: "overdue", label: "Overdue for renewal" },
];

type AudienceRangeFilterId =
  "employeeRangeIds" | "memberSinceRangeIds" | "renewalRangeIds";
const audienceRangeOptions = {
  employeeRangeIds: {
    current: memberSizeOptions,
    legacy: legacyMemberSizeOptions,
  },
  memberSinceRangeIds: {
    current: memberSinceOptions,
    legacy: legacyMemberSinceOptions,
  },
  renewalRangeIds: {
    current: renewalStatusOptions,
    legacy: legacyRenewalStatusOptions,
  },
};

// Only saved legacy choices stay available. Selecting a new range never changes
// the meaning of a saved choice; staff can remove that choice explicitly.
type AudienceRangeOptionsInput = {
  filterId: AudienceRangeFilterId;
  values: string[];
};

export const getAudienceRangeOptions = ({
  filterId,
  values,
}: AudienceRangeOptionsInput) => {
  const options = audienceRangeOptions[filterId];
  return [
    ...options.current,
    ...values
      .filter((value) => !options.current.some((option) => option.id === value))
      .map((value) => ({
        id: value,
        label: `${options.legacy.find((option) => option.id === value.replace(/^legacy:/, ""))?.label || value} (legacy)`,
      })),
  ];
};

export const formatNumber = (value: number) => value.toLocaleString();

const getEmployeeCount = (member: EmailAudienceMemberOption) =>
  (member.fullTimeEmployees || 0) + (member.partTimeEmployees || 0);

const isWithinEmployeeRange = (rangeId: string, count: number) => {
  if (rangeId === "100+") return count >= 101;
  rangeId = rangeId.replace(/^(legacy:|employees:)/, "");
  if (rangeId === "1") return count === 1;
  if (rangeId === "individual") return count <= 1;
  if (rangeId.endsWith("+")) return count >= Number(rangeId.replace("+", ""));
  const [from, to] = rangeId.split("-").map(Number);
  return count >= from && count <= to;
};

const getYearsSince = (dateUtcMilli?: number | null) =>
  dateUtcMilli
    ? (Date.now() - dateUtcMilli) / (365.25 * 24 * 60 * 60 * 1000)
    : null;

const isWithinYearRange = (rangeId: string, years: number | null) => {
  if (years === null || years < 0) return false;
  const isLegacy = !rangeId.startsWith("years:");
  rangeId = rangeId.replace(/^(legacy:|years:)/, "");
  if (rangeId === "<1") return years < 1;
  if (rangeId.endsWith("+")) return years >= Number(rangeId.replace("+", ""));
  const [from, to] = rangeId.split("-").map(Number);
  return years >= from && years < to + (isLegacy ? 1 : 0);
};

const getDaysUntil = (dateUtcMilli?: number | null) =>
  dateUtcMilli ? (dateUtcMilli - Date.now()) / (24 * 60 * 60 * 1000) : null;

export const isWithinRenewalRange = (rangeId: string, days: number | null) => {
  if (days === null) return false;
  if (rangeId === "overdue") return days < 0;
  if (rangeId.startsWith("within:"))
    return days >= 0 && days < Number(rangeId.slice("within:".length));
  rangeId = rangeId.replace(/^legacy:/, "");
  days = Math.ceil(days);
  if (rangeId === "<30") return days >= 0 && days < 30;
  if (rangeId === "91+") return days >= 91;
  const [from, to] = rangeId.split("-").map(Number);
  return days >= from && days <= to;
};

export const getFilteredMemberIds = ({
  filters,
  memberOptions,
}: {
  filters: AudienceFilters;
  memberOptions: EmailAudienceMemberOption[];
}) => {
  return memberOptions
    .filter((member) => {
      const memberTypeMatch =
        filters.membershipTypeIds.length === 0 ||
        filters.membershipTypeIds.includes(member.membershipTypeId);
      const memberSizeMatch =
        filters.employeeRangeIds.length === 0 ||
        filters.employeeRangeIds.some((rangeId) =>
          isWithinEmployeeRange(rangeId, getEmployeeCount(member))
        );
      const industryMatch =
        filters.categoryIds.length === 0 ||
        filters.categoryIds.some((categoryId) =>
          (member.categoryId || []).includes(categoryId)
        );
      const memberSinceMatch =
        filters.memberSinceRangeIds.length === 0 ||
        filters.memberSinceRangeIds.some((rangeId) =>
          isWithinYearRange(rangeId, getYearsSince(member.dateJoinedUtcMilli))
        );
      const renewalMatch =
        filters.renewalRangeIds.length === 0 ||
        filters.renewalRangeIds.some(
          (rangeId) =>
            (rangeId !== "overdue" || member.lifecycleStatus === "ACTIVE") &&
            isWithinRenewalRange(
              rangeId,
              getDaysUntil(member.renewalDateUtcMilli)
            )
        );

      return (
        memberTypeMatch &&
        memberSizeMatch &&
        industryMatch &&
        memberSinceMatch &&
        renewalMatch
      );
    })
    .map((member) => member.id);
};

export const memberMatchesRecipientSource = ({
  member,
  sourceId,
}: {
  member: EmailAudienceMemberOption;
  sourceId: RecipientSourceId;
}): boolean => {
  if (sourceId === "allMembers") {
    return member.lifecycleStatus !== "DELETED";
  }
  if (sourceId === "activeMembers") {
    return member.lifecycleStatus === "ACTIVE";
  }
  if (sourceId === "newMembers") {
    const nowUtcMilli = Date.now();
    return (
      member.lifecycleStatus === "ACTIVE" &&
      !!member.dateJoinedUtcMilli &&
      member.dateJoinedUtcMilli <= nowUtcMilli &&
      member.dateJoinedUtcMilli >= nowUtcMilli - 60 * 24 * 60 * 60 * 1000
    );
  }
  if (sourceId === "renewingMembers") {
    const daysUntilRenewal = getDaysUntil(member.renewalDateUtcMilli);
    return (
      member.lifecycleStatus === "ACTIVE" &&
      daysUntilRenewal !== null &&
      daysUntilRenewal >= 0 &&
      daysUntilRenewal <= 60
    );
  }
  // Prospects are resolved from the dedicated prospect module, not from
  // ChamberMember lifecycle rows. Only the backend preview can count them.
  return false;
};

// Step 4 Audience Summary rows: each Step-1 selection becomes one bordered row
// with its own real count. `count: null` means the total lives on the backend
// (for example website subscribers) and the row renders an em dash instead of
// a made-up number.
export type AudienceSummaryRow = {
  id: string;
  label: string;
  count: number | null;
};

export const buildRecipientSourceRows = (
  preview: RecipientPreviewSummary
): Array<AudienceSummaryRow & { count: number }> | null => {
  if (!Array.isArray(preview.recipients)) return null;
  const sources = new Map<string, { id: string; label: string; count: number }>();
  const sourceByEmail = new Map<string, string>();
  for (const recipient of preview.recipients) {
    if (recipient.status !== CmpNewsletterRecipientPreviewRecipientStatusEnum.Valid) continue;
    // Use intended addresses, because test routing can use one actual inbox.
    const address = (recipient.intendedEmail || recipient.email || "").trim().toLowerCase();
    if (!address || !recipient.sourceLabel.trim()) return null;
    const sourceId = JSON.stringify([recipient.sourceKind, recipient.sourceLabel]);
    const existingSource = sourceByEmail.get(address);
    if (existingSource && existingSource !== sourceId) return null;
    if (existingSource) continue;
    sourceByEmail.set(address, sourceId);
    const source = sources.get(sourceId);
    if (source) source.count += 1;
    else sources.set(sourceId, { id: sourceId, label: recipient.sourceLabel, count: 1 });
  }
  // A partial preview must not look like a complete audience breakdown.
  if (sourceByEmail.size !== preview.uniqueValidRecipientCount) return null;
  return Array.from(sources.values());
};

const backendOwnedRecipientCountSourceIds = new Set<RecipientSourceId>([
  "allMembers",
  "activeMembers",
  "activePrimaryContacts",
  "nonMemberSubscribers",
  "prospectMembers",
  "eventAttendees",
  "pastEventAttendees",
  "eblastSubscribers",
  "governanceGroups",
  "membersOfferingPerks",
  "pendingMembers",
  "suspendedMembers",
  "eventSponsors",
]);

export const recipientCountRequiresBackendPreview = (
  sourceIds: RecipientSourceId[]
): boolean =>
  sourceIds.some((sourceId) =>
    backendOwnedRecipientCountSourceIds.has(sourceId)
  );

export const resolveIntendedRecipientCount = ({
  fallbackCount,
  hasRecipients,
  isSentRecord,
  previewCount,
  requiresBackendPreview,
  storedCount,
}: {
  fallbackCount: number;
  hasRecipients: boolean;
  isSentRecord: boolean;
  previewCount: number | null | undefined;
  requiresBackendPreview: boolean;
  storedCount: number;
}): number | null => {
  if (!hasRecipients) return 0;
  if (isSentRecord) return storedCount;
  // A successful backend preview of zero is authoritative.
  if (previewCount !== null && previewCount !== undefined) return previewCount;
  if (requiresBackendPreview) return null;
  return fallbackCount;
};

const recipientSourceSummaryLabels: Partial<Record<RecipientSourceId, string>> =
  {
    allMembers: "Full Email Subscribers Lists",
    activeMembers: "Active Members",
    activePrimaryContacts: "Primary Contact of Active Members",
    nonMemberSubscribers: nonMemberSubscribersRecipientSourceLabel,
    newMembers: "New Members (within 60 days)",
    renewingMembers: "Renewing Members (within 60 days)",
    prospectMembers: "Prospect Members",
    eblastSubscribers: "E-Blast Subscribers",
    eventAttendees: "Event Attendees",
    pastEventAttendees: "Past Event Attendees",
    governanceGroups: "Governance Groups",
    membersOfferingPerks: "Members Offering Perks",
    pendingMembers: "Pending Members",
    suspendedMembers: "Suspended Members",
    eventSponsors: "Sponsors",
  };

const audienceFilterSummaryPrefixes: Record<AudienceFilterId, string> = {
  membershipTypeIds: "Membership Type",
  employeeRangeIds: "Member Size",
  categoryIds: "Industry",
  memberSinceRangeIds: "Member Since",
  renewalRangeIds: "Renewal Status",
};

const prettifyEnumLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const countMembersForRecipientSource = (
  sourceId: RecipientSourceId,
  memberOptions: EmailAudienceMemberOption[]
) =>
  memberOptions.filter((member) =>
    memberMatchesRecipientSource({ member, sourceId })
  ).length;

const countMembersForFilterValue = (
  filterId: AudienceFilterId,
  valueId: string,
  memberOptions: EmailAudienceMemberOption[]
) =>
  memberOptions.filter((member) => {
    if (filterId === "membershipTypeIds")
      return member.membershipTypeId === valueId;
    if (filterId === "employeeRangeIds")
      return isWithinEmployeeRange(valueId, getEmployeeCount(member));
    if (filterId === "categoryIds")
      return (member.categoryId || []).includes(valueId);
    if (filterId === "memberSinceRangeIds")
      return isWithinYearRange(
        valueId,
        getYearsSince(member.dateJoinedUtcMilli)
      );
    return (
      (valueId !== "overdue" || member.lifecycleStatus === "ACTIVE") &&
      isWithinRenewalRange(valueId, getDaysUntil(member.renewalDateUtcMilli))
    );
  }).length;

const getFilterValueLabel = (
  filterId: AudienceFilterId,
  valueId: string,
  industryLabels: Record<string, string>
) => {
  if (filterId === "membershipTypeIds")
    return industryLabels[`membershipType:${valueId}`] || valueId;
  if (filterId === "categoryIds")
    return industryLabels[valueId] || prettifyEnumLabel(valueId);
  return (
    getAudienceRangeOptions({ filterId, values: [valueId] }).find(
      (option) => option.id === valueId
    )?.label || valueId
  );
};

export const buildAudienceFilterSummary = ({
  audienceFilters,
  industryLabels = {},
}: {
  audienceFilters: AudienceFilters;
  industryLabels?: Record<string, string>;
}): string =>
  (Object.keys(audienceFilterSummaryPrefixes) as AudienceFilterId[])
    .filter((filterId) => audienceFilters[filterId].length > 0)
    .map(
      (filterId) =>
        `${audienceFilterSummaryPrefixes[filterId]}: ${audienceFilters[filterId]
          .map((valueId) => getFilterValueLabel(filterId, valueId, industryLabels))
          .join(", ")}`
    )
    .join("; ");

export const buildAudienceSummaryRows = ({
  email,
  recipientGroups,
  memberOptions,
  audienceFilters,
  industryLabels = {},
}: {
  email: EmailFormState;
  recipientGroups: RecipientGroup[];
  memberOptions: EmailAudienceMemberOption[];
  audienceFilters: AudienceFilters;
  industryLabels?: Record<string, string>;
}): AudienceSummaryRow[] => {
  const rows: AudienceSummaryRow[] = [];

  email.recipientSourceIds.forEach((sourceId) => {
    const label = recipientSourceSummaryLabels[sourceId];
    if (!label) return;
    if (sourceId === "activeMembers" || sourceId === "activePrimaryContacts") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        count: null,
      });
      return;
    }
    if (sourceId === "allMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        // Full Email also includes current nonmember website subscribers,
        // which are intentionally not loaded into the dashboard member query.
        count: null,
      });
      return;
    }
    if (sourceId === "prospectMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        // Prospects live in their own module and are counted by backend preview.
        count: null,
      });
      return;
    }
    if (sourceId === "newMembers" || sourceId === "renewingMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        count: countMembersForRecipientSource(sourceId, memberOptions),
      });
      return;
    }
    rows.push({ id: `source-${sourceId}`, label, count: null });
  });

  recipientGroups
    .filter((group) => email.recipientGroupIds.includes(group.id))
    .forEach((group) => {
      rows.push({
        id: `group-${group.id}`,
        label: group.name,
        count: group.count,
      });
    });

  (Object.keys(audienceFilters) as AudienceFilterId[]).forEach((filterId) => {
    audienceFilters[filterId].forEach((valueId) => {
      rows.push({
        id: `filter-${filterId}-${valueId}`,
        label: `${audienceFilterSummaryPrefixes[filterId]}: ${getFilterValueLabel(
          filterId,
          valueId,
          industryLabels
        )}`,
        count: countMembersForFilterValue(filterId, valueId, memberOptions),
      });
    });
  });

  const hasFilterSelections = Object.values(audienceFilters).some(
    (values) => values.length > 0
  );
  if (
    !hasFilterSelections &&
    email.recipientSourceIds.includes("selectedMembers") &&
    email.selectedMemberIds.length > 0
  ) {
    rows.push({
      id: "source-selectedMembers",
      label: "Selected Members",
      count: email.selectedMemberIds.length,
    });
  }

  return rows;
};

type SectionHeadingProps = {
  index: number;
  title: React.ReactNode;
  description?: string;
  compact?: boolean;
};

export const SectionHeading = ({
  index,
  title,
  description,
  compact = false,
}: SectionHeadingProps) => (
  <div className={`${compact ? "mb-2.5" : "mb-6"} flex items-start gap-3`}>
    <span
      className={`${compact ? "h-[26px] w-[26px] text-[13px] font-bold" : "mt-1 h-8 w-8 text-[15px] font-extrabold"} flex shrink-0 items-center justify-center rounded-full bg-cpaGold text-white`}
    >
      {index}
    </span>
    <div>
      <h2
        className={
          compact
            ? "text-[16px] font-semibold leading-[26px] text-[#0A0A0A]"
            : sectionTitleClassName
        }
      >
        {title}
      </h2>
      {description ? (
        <p
          className={
            compact
              ? "mt-2.5 text-[14px] text-[#0A0A0A]"
              : sectionDescriptionClassName
          }
        >
          {description}
        </p>
      ) : null}
    </div>
  </div>
);

export const Field = ({
  label,
  helper,
  required,
  children,
  appearance,
}: {
  label: React.ReactNode;
  helper?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  appearance?: "campaignDetails";
}) => (
  <label className="block">
    <span
      className={
        appearance === "campaignDetails"
          ? campaignFieldLabelClassName
          : fieldLabelClassName
      }
    >
      {label}
      {required ? (
        <span
          className={
            appearance === "campaignDetails" ? "text-[#E24C4C]" : "text-red-500"
          }
        >
          {" "}
          *
        </span>
      ) : null}
    </span>
    {helper ? (
      <span
        className={
          appearance === "campaignDetails"
            ? campaignFieldHelperClassName
            : fieldHelperClassName
        }
      >
        {helper}
      </span>
    ) : null}
    <div className={appearance === "campaignDetails" ? "mt-2" : "mt-3"}>
      {children}
    </div>
  </label>
);

export const MetricCard = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <div className="rounded-xl border border-gray-200 bg-white px-6 py-5">
    <p className="text-[13px] font-extrabold leading-5 text-gray-950">
      {label}
    </p>
    <p className="mt-3 text-[28px] font-extrabold leading-none text-black">
      {value}
    </p>
  </div>
);

export type FilterDropdownProps = {
  label: string;
  placeholder: string;
  options: Array<{ id: string; label: string }>;
  values: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
  compact?: boolean;
  showChips?: boolean;
  selectionDisplay?: "summary" | "count";
  chipAppearance?: "savedAudience";
  singleSelect?: boolean;
  additiveSinglePick?: boolean;
  disabled?: boolean;
  className?: string;
  leadingIcon?: React.ReactNode;
  searchPlaceholder?: string;
  rowActions?: (option: { id: string; label: string }) => React.ReactNode;
};

export const FilterDropdown = ({
  label,
  placeholder,
  options,
  values,
  onChange,
  searchable = true,
  compact = false,
  showChips = false,
  selectionDisplay = "summary",
  chipAppearance,
  singleSelect = false,
  additiveSinglePick = false,
  disabled = false,
  className = "",
  leadingIcon,
  searchPlaceholder,
  rowActions,
}: FilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const selected = options.filter((option) => values.includes(option.id));
  const visibleOptions = options.filter(
    (option) =>
      (!additiveSinglePick || !!rowActions || !values.includes(option.id)) &&
      option.label.toLowerCase().includes(search.trim().toLowerCase())
  );
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  const toggle = (value: string) => {
    if (disabled) return;
    if (singleSelect || additiveSinglePick) {
      if (additiveSinglePick) {
        if (!values.includes(value)) onChange([...values, value]);
      } else {
        onChange([value]);
      }
      setOpen(false);
      buttonRef.current?.focus();
    } else
      onChange(
        values.includes(value)
          ? values.filter((id) => id !== value)
          : [...values, value]
      );
  };
  return (
    <div className={`relative min-w-0 ${className}`} ref={containerRef}>
      {!compact && (
        <p className="mb-2 text-[14px] font-semibold text-[#0A0A0A]">{label}</p>
      )}
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="dialog"
        disabled={disabled}
        className={`${inputClassName} flex items-center justify-between gap-2 text-left`}
        onClick={() => {
          setOpen(!open);
          setSearch("");
        }}
      >
        {leadingIcon && (
          <span className="flex shrink-0 items-center">{leadingIcon}</span>
        )}
        <span
          className={`min-w-0 flex-1 truncate ${selected.length ? "text-[#0A0A0A]" : "text-[#9E9E9E]"}`}
        >
          {!selected.length
            ? placeholder
            : selected.length === 1 || selectionDisplay === "summary"
              ? selected[0].label
              : `${selected.length} selected`}
        </span>
        {selectionDisplay === "summary" && selected.length > 1 && (
          <span className="shrink-0 text-[#0A0A0A]">+{selected.length - 1}</span>
        )}
        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
      </button>
      {open && (
        <div
          id={menuId}
          role="dialog"
          aria-label={`${label} options`}
          className="absolute z-30 mt-2 max-h-80 w-full min-w-[180px] overflow-y-auto rounded-lg border border-[#ECECEC] bg-white p-3 shadow-xl"
        >
          {searchable && (
            <input
              autoFocus
              aria-label={`Search ${label}`}
              placeholder={searchPlaceholder || `Search ${label}`}
              className={`${inputClassName} mb-2`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          )}
          {!singleSelect && !additiveSinglePick && (
            <label className="flex cursor-pointer items-center gap-2.5 py-2 text-[14px]">
              <Checkbox
                sx={inlineChoiceControlSx}
                checked={
                  options.length > 0 &&
                  options.every((option) => values.includes(option.id))
                }
                onChange={() =>
                  onChange(
                    options.every((option) => values.includes(option.id))
                      ? []
                      : options.map((option) => option.id)
                  )
                }
              />
              Select All
            </label>
          )}
          {visibleOptions.map((option) => {
            const choiceProps = {
              checked: values.includes(option.id),
              disabled,
              onChange: () => toggle(option.id),
              sx: inlineChoiceControlSx,
            };
            return (
            <div key={option.id} className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 py-2 text-[14px]">
              {singleSelect || additiveSinglePick
                ? <Radio {...choiceProps} name={menuId} />
                : <Checkbox {...choiceProps} />}
              {option.label}
            </label>
            {rowActions?.(option)}
            </div>
            );
          })}
          {!visibleOptions.length && (
            <p className="py-2 text-sm text-[#727272]">No matching options.</p>
          )}
        </div>
      )}
      {showChips && selected.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selected.map((option) => (
            <span
              key={option.id}
              className={`inline-flex max-w-full items-center rounded-2xl border border-cpaGold bg-[#FDF8EE] pl-3 text-[13px] ${chipAppearance === "savedAudience" ? "gap-1.5 py-[5px] pr-2 font-medium" : "gap-1"}`}
            >
              <span className="truncate">{option.label}</span>
              <button
                type="button"
                aria-label={`Remove ${option.label}`}
                disabled={disabled}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full md:h-8 md:w-8"
                onClick={() =>
                  onChange(values.filter((id) => id !== option.id))
                }
              >
                {chipAppearance === "savedAudience" ? (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-cpaGold" aria-hidden="true">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.4" strokeLinecap="round">
                      <path d="m6 6 12 12M18 6 6 18" />
                    </svg>
                  </span>
                ) : (
                  <CloseIcon sx={{ fontSize: 16 }} />
                )}
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
